import React from "react"
import { Form, Select, Space, FormInstance } from "antd"
import { faChartSimple, faGear } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import FormatPanelHeader from "./FormatPanelHeader.component"
import { EXPORT_VARIABLE_TYPES, SPSSFormValues } from "types/engineExport.types"
import { InputValues } from "features/QueryConsole/api/useExportDataModal"

interface SpssExportPanelProps {
  inputValues: InputValues
  onInputChange: <K extends keyof InputValues>(key: K, value: InputValues[K]) => void
  onExportSPS: () => Promise<void>
  onExportSAV: () => Promise<void>
  isExportingSPS: boolean
  isExportingSAV: boolean
  form: FormInstance<SPSSFormValues>
  onOpenExportMap: (format: "sps" | "sav") => void
}

const SpssExportPanel: React.FC<SpssExportPanelProps> = ({
  inputValues,
  onInputChange,
  onExportSPS,
  onExportSAV,
  isExportingSPS,
  isExportingSAV,
  form,
  onOpenExportMap,
}) => (
  <div className="flex flex-col">
    <FormatPanelHeader
      icon={faChartSimple}
      title="Export as SPSS"
      tagLabel="Statistical"
      tagColor="green"
      description="For IBM SPSS Statistics. SPS+DAT produces syntax and data files; SAV produces a single binary file you can open directly."
    />
    <Form layout="vertical" form={form}>
      <Form.Item
        label="Select Variable Type"
        name="variableType"
        rules={[{ required: true, message: "Please select a Variable Type" }]}
      >
        <Select
          value={inputValues.variableType || undefined}
          onChange={(value: EXPORT_VARIABLE_TYPES) =>
            onInputChange("variableType", value)
          }
          options={[
            { value: EXPORT_VARIABLE_TYPES.NUMBERING, label: "Numbered" },
            { value: EXPORT_VARIABLE_TYPES.LABELS, label: "Labelled" },
          ]}
          placeholder="Select Variable Type"
        />
      </Form.Item>
      <Form.Item label="Output Format">
        <Select
          value={inputValues.spsOutputFormat}
          onChange={(value: "sps" | "sav") =>
            onInputChange("spsOutputFormat", value)
          }
          options={[
            { value: "sps", label: "SPS + DAT (SPSS Syntax)" },
            { value: "sav", label: "SAV (SPSS Binary)" },
          ]}
        />
      </Form.Item>
    </Form>
    <div className="border-t border-gray-100 pt-4 mt-1">
      <Space wrap>
        <DataQButton
          type="primary"
          onClick={inputValues.spsOutputFormat === "sav" ? onExportSAV : onExportSPS}
          isLoading={inputValues.spsOutputFormat === "sav" ? isExportingSAV : isExportingSPS}
          aria-label={inputValues.spsOutputFormat === "sav" ? "Export data as SAV file" : "Export data as SPSS file"}
        >
          {inputValues.spsOutputFormat === "sav" ? "Export SAV" : "Export SPSS"}
        </DataQButton>
        <DataQButton
          type="secondary"
          onClick={() => onOpenExportMap(inputValues.spsOutputFormat)}
        >
          <FontAwesomeIcon icon={faGear} className="mr-1" />
          Configure Map
        </DataQButton>
      </Space>
    </div>
  </div>
)

export default SpssExportPanel
